import React from 'react';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';
import { FileText } from 'lucide-react';

const TermsOfService = () => {
  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-brand-bg pt-24 px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-violet-600/20 rounded-full mb-4">
              <FileText size={32} className="text-violet-500" />
            </div>
            <h1 className="text-5xl font-outfit font-light tracking-tight text-white mb-3">
              Terms of Service
            </h1>
            <p className="text-zinc-400">
              Last updated: December 2024
            </p>
          </div>

          {/* Content */}
          <div className="bg-brand-surface border border-brand-border rounded-2xl p-8 md:p-12 space-y-8">
            <p className="text-zinc-300 leading-relaxed">
              Welcome to our website. By accessing or using our services, you agree to be bound by these Terms of Service ("Terms"). Please read them carefully.
            </p>

            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-outfit font-semibold text-white mb-4">
                1. Nature of Service
              </h2>
              <div className="space-y-3 text-zinc-300 leading-relaxed">
                <p>
                  We provide a custom printing service for user-requested designs, including playtest cards intended for casual, non-commercial use.
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>We do not sell official trading cards.</li>
                  <li>All products are custom-printed items created based on user-provided or user-selected content.</li>
                </ul>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-outfit font-semibold text-white mb-4">
                2. No Affiliation or Endorsement
              </h2>
              <div className="space-y-3 text-zinc-300 leading-relaxed">
                <p>
                  This website and its services are not affiliated with, endorsed, sponsored, or specifically approved by Wizards of the Coast.
                </p>
                <p>
                  Magic: The Gathering and all related names, symbols, and imagery are trademarks and/or copyrighted material owned by Wizards of the Coast LLC.
                </p>
                <p>
                  All such intellectual property remains the sole property of its respective owners.
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-outfit font-semibold text-white mb-4">
                3. Intended Use
              </h2>
              <div className="space-y-4 text-zinc-300 leading-relaxed">
                <div>
                  <p className="font-semibold text-white mb-2">All products sold are intended exclusively for:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Personal use</li>
                    <li>Casual play</li>
                    <li>Deck testing (playtesting)</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-white mb-2">Our products are NOT intended for:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Official tournaments</li>
                    <li>Sanctioned events</li>
                    <li>Resale as authentic cards</li>
                    <li>Misrepresentation as genuine products</li>
                  </ul>
                </div>
                <p className="text-yellow-400 bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4 mt-4">
                  ⚠ You agree not to use our products in any way that could mislead others into believing they are official or authentic cards.
                </p>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-outfit font-semibold text-white mb-4">
                4. User Responsibility
              </h2>
              <div className="space-y-3 text-zinc-300 leading-relaxed">
                <p>By submitting or selecting content for printing, you confirm that:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>You understand the nature of custom playtest cards</li>
                  <li>You will not use the products for fraudulent or deceptive purposes</li>
                  <li>You accept full responsibility for how the printed items are used</li>
                </ul>
                <p className="mt-4">
                  We are not responsible for any misuse of the products after delivery.
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-outfit font-semibold text-white mb-4">
                5. Intellectual Property
              </h2>
              <div className="space-y-3 text-zinc-300 leading-relaxed">
                <p>
                  We do not claim ownership over any third-party intellectual property displayed in user-selected content.
                </p>
                <p>
                  All trademarks, card names, artwork, and related elements belong to their respective rights holders, including Wizards of the Coast.
                </p>
                <p>
                  Our service acts solely as a printing intermediary based on user input.
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-outfit font-semibold text-white mb-4">
                6. Custom Content Disclaimer
              </h2>
              <div className="space-y-3 text-zinc-300 leading-relaxed">
                <p>
                  Users may request specific card designs, versions, or artwork.
                </p>
                <p>We do not guarantee:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Accuracy of artwork or card details</li>
                  <li>Exact replication of official printing standards</li>
                  <li>Availability of all versions or editions</li>
                </ul>
                <p className="mt-4">
                  All products are clearly distinguishable from official cards.
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-outfit font-semibold text-white mb-4">
                7. Limitation of Liability
              </h2>
              <div className="space-y-3 text-zinc-300 leading-relaxed">
                <p>To the fullest extent permitted by law:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>We are not liable for any damages arising from the use or misuse of our products</li>
                  <li>We are not responsible for legal consequences resulting from improper use</li>
                  <li>Our liability is limited to the amount paid for the order</li>
                </ul>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-outfit font-semibold text-white mb-4">
                8. Refusal of Service
              </h2>
              <div className="space-y-3 text-zinc-300 leading-relaxed">
                <p>We reserve the right to refuse or cancel any order that:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Violates these Terms</li>
                  <li>Raises legal concerns</li>
                  <li>Appears to be intended for fraudulent use</li>
                </ul>
              </div>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-2xl font-outfit font-semibold text-white mb-4">
                9. Modifications
              </h2>
              <p className="text-zinc-300 leading-relaxed">
                We may update these Terms at any time. Continued use of the service constitutes acceptance of any changes.
              </p>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-2xl font-outfit font-semibold text-white mb-4">
                10. Governing Law
              </h2>
              <p className="text-zinc-300 leading-relaxed">
                These Terms shall be governed by and interpreted in accordance with the laws of the applicable jurisdiction in which the business operates.
              </p>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-2xl font-outfit font-semibold text-white mb-4">
                11. Contact
              </h2>
              <p className="text-zinc-300 leading-relaxed">
                For any questions regarding these Terms, please contact us through our{' '}
                <a href="/contact" className="text-violet-400 hover:text-violet-300 underline">
                  Contact page
                </a>.
              </p>
            </section>

            {/* Acceptance */}
            <div className="pt-8 border-t border-zinc-800">
              <p className="text-zinc-400 text-sm leading-relaxed">
                By using this website, you acknowledge that you have read, understood, and agree to these Terms of Service.
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TermsOfService;
